/* ============================================================================
   NEOGEN — CART DRAWER JS
   ----------------------------------------------------------------------------
   Ported 2026-07-07 (Sonnet) from the staged skeleton:
   codex-handoffs/outbound/2026-07-07-liquid-scaffold/assets/neogen-cart-contract.js
   Markup source: snippets/neogen-cart-drawer.liquid (mockup:
   2026-05-15-cart-drawer-mockup-r2-bestof.html).

   WHAT'S REAL HERE:
     · Open/close lifecycle with a focus TRAP, ESC to close, focus RESTORE to
       the invoking control, background made inert (aria-hidden + [inert])
       while the dialog is open, overlay-click-to-close.
     · Live Shopify AJAX: GET /cart.js, POST /cart/change.js (qty + remove).
       Cart contents, subtotal, checkout amount, and every [data-cart-count]
       badge on the page are rebuilt from the live cart on every change.
     · Errors surface visibly in [data-cart-error] (no silent failures); a
       polite [data-cart-status] region announces success to screen readers.
     · prefers-reduced-motion collapses the open/close transition timing.
     · Subscribe-&-save UI model: flat 20% recurring, cadence 45/60/75/90
       (default 60). Client-side price preview only — see STUB below. This
       block only ever renders when settings.subscriptions_enabled is true
       (post-launch flag; see snippets/neogen-cart-drawer.liquid).

   WHAT'S STUBBED (deferred — requires the subscription app + its selling
   plans, which do not exist yet; SONNET-LIQUID-BACKLOG §H):
     · Binding the subscribe toggle / cadence choice to a real selling_plan
       ID on POST /cart/change.js, so the 20% + cadence persist to checkout.
       Marked `STUB:` inline at both call sites below.

   INTEGRATION NOTE for whoever wires PDP add-to-cart into this drawer:
     assets/product-variants.js currently does
       this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
       ... else if (!this.cart) { window.location = window.routes.cart_url; return; }
     This NEOGEN drawer is not a <cart-drawer> custom element, so until that
     lookup (or the old <cart-drawer> component) is updated, a PDP add-to-cart
     will still hard-redirect to /cart instead of opening this drawer. This
     file listens for the theme's existing PUB_SUB_EVENTS.cartUpdate /
     cartError (assets/pubsub.js + assets/global.js) as the safest hook that
     doesn't require editing files outside this port's ownership — if that
     event fires, this drawer opens and refreshes correctly. Whoever finishes
     the PDP-to-drawer wiring should confirm cartUpdate still publishes (i.e.
     that the `!this.cart` branch isn't hit first).

   Contract (markup the drawer snippet provides — see interaction-contracts.md §2):
     <aside data-cart-drawer role="dialog" aria-modal="true" hidden> … </aside>
     [data-cart-scrim]          — backdrop, click closes
     [data-cart-link]           — any Bag control that opens the drawer (progressive
                                   enhancement: without JS the link goes to /cart)
     [data-cart-close]          — close control(s) inside the drawer
     [data-cart-body]           — line-item container, rebuilt on refresh
     [data-cart-filled]         — regions shown only when the cart has items
     [data-cart-empty]          — region shown only when the cart is empty
     [data-line-item]           — each line row; data-line-key = cart item key
       [data-quantity-decrease] / [data-quantity-increase] / [data-quantity-value]
       [data-line-remove]
       [data-sub-toggle]        — subscribe checkbox (subscriptions_enabled only)
       [data-cadence]           — 45|60|75|90 buttons (aria-pressed)
       [data-cadence-group]     — cadence row, hidden until subscribed
       [data-price], [data-price-sub] — list + member price nodes
     [data-cart-subtotal]       — subtotal node
     [data-cart-checkout-amount]— amount echoed on the checkout button
     [data-cart-count]          — badge(s) anywhere on the page
     [data-cart-error]          — visible error region (role="alert")
     [data-cart-status]         — visually-hidden polite live region
   ============================================================================ */
(function () {
  'use strict';

  var config = window.neogenCartConfig || {};
  var SUB_RATE = typeof config.subRate === 'number' ? config.subRate : 0.20; // flat 20% recurring — NOT a sale
  var CADENCES = config.cadences || [45, 60, 75, 90];                        // days
  var DEFAULT_CADENCE = config.defaultCadence || 60;
  var ROUTES = config.routes || {};
  var CART_URL = (ROUTES.cartUrl || '/cart').replace(/\/$/, '');
  var CART_JSON_URL = CART_URL + '.js';
  var CART_CHANGE_URL = ROUTES.cartChangeUrl || '/cart/change.js';
  var CART_ADD_URL = ROUTES.cartAddUrl || '/cart/add.js';

  var drawer = document.querySelector('[data-cart-drawer], aside.drawer[aria-modal="true"]');
  var scrim = document.querySelector('[data-cart-scrim]');
  var lastFocused = null;
  var closeTimer = null;
  var inertEls = [];

  /* ---------- motion ------------------------------------------------------ */
  function motionOK() {
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- money -------------------------------------------------------- */
  function money(cents) {
    var amount = (Number(cents) || 0) / 100;
    if (typeof window.formatPrice === 'function' && window.theme && window.theme.settings && window.theme.settings.money_format) {
      return window.formatPrice(window.theme.settings.money_format, amount);
    }
    var format = config.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount.toFixed(2));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- status / error regions --------------------------------------- */
  function showError(message) {
    var el = drawer && drawer.querySelector('[data-cart-error]');
    if (!el) { window.console && console.error('[neogen-cart]', message); return; }
    el.textContent = message;
    el.hidden = false;
  }
  function clearError() {
    var el = drawer && drawer.querySelector('[data-cart-error]');
    if (el) { el.hidden = true; el.textContent = ''; }
  }
  function announce(message) {
    var el = drawer && drawer.querySelector('[data-cart-status]');
    if (el) el.textContent = message;
  }

  /* ---------- focus trap ---------------------------------------------------- */
  function focusables(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }
  function trap(e) {
    if (!drawer || drawer.hasAttribute('hidden')) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeCart(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables(drawer);
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  }

  /* ---------- background inert while modal is open -------------------------- */
  function setBackgroundInert(on) {
    if (on) {
      inertEls = Array.prototype.slice.call(document.body.children).filter(function (el) {
        return el !== drawer && el !== scrim &&
          !(drawer && el.contains(drawer)) && !(scrim && el.contains(scrim)) &&
          !el.hasAttribute('hidden');
      });
      inertEls.forEach(function (el) {
        if (!el.hasAttribute('aria-hidden')) el.setAttribute('data-neogen-cart-added-aria-hidden', '1');
        el.setAttribute('aria-hidden', 'true');
        if ('inert' in el) el.inert = true;
      });
    } else {
      inertEls.forEach(function (el) {
        if (el.hasAttribute('data-neogen-cart-added-aria-hidden')) {
          el.removeAttribute('aria-hidden');
          el.removeAttribute('data-neogen-cart-added-aria-hidden');
        }
        if ('inert' in el) el.inert = false;
      });
      inertEls = [];
    }
  }

  /* ---------- open / close lifecycle ---------------------------------------- */
  var isOpen = false;

  function openCart() {
    if (!drawer) return;
    // Re-entrant safe: add-to-cart success can invoke this both directly
    // (product-variants.js) and via the cartUpdate subscriber below. If the
    // drawer is already open(ing), just re-hydrate — re-running the open
    // sequence would rebuild the inert list mid-flight (stranding aria-hidden
    // on close) and overwrite the stored invoker focus with a drawer-internal
    // element, breaking focus-restore.
    if (isOpen) { refreshCart(); return; }
    isOpen = true;
    lastFocused = document.activeElement;
    clearTimeout(closeTimer);
    drawer.removeAttribute('hidden');
    if (scrim) scrim.removeAttribute('hidden');
    setBackgroundInert(true);
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', trap, true);

    function activate() {
      if (!isOpen) return; // closed again before the enter frame — abort
      drawer.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      var f = focusables(drawer);
      (f[0] || drawer).focus();
    }
    if (motionOK()) {
      void drawer.offsetWidth; // force reflow so the enter transition runs from off-screen
      requestAnimationFrame(function () { requestAnimationFrame(activate); });
    } else {
      activate();
    }
    refreshCart();
  }

  function closeCart() {
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    if (scrim) scrim.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', trap, true);
    setBackgroundInert(false);
    // Hide only after the exit transition really ends. The old fixed 320ms
    // timer undercut the 480ms slide (--nc-dur-slow) — display:none landed
    // two-thirds through and the drawer visibly stuttered out (Craig R2 item 6).
    var finishClose = function () {
      if (isOpen) return; // reopened mid-exit — leave it visible
      drawer.removeEventListener('transitionend', onExitEnd);
      drawer.setAttribute('hidden', '');
      if (scrim) scrim.setAttribute('hidden', '');
    };
    var onExitEnd = function (e) {
      if (e.target !== drawer || e.propertyName !== 'transform') return;
      finishClose();
    };
    clearTimeout(closeTimer);
    if (motionOK()) {
      drawer.addEventListener('transitionend', onExitEnd);
      closeTimer = setTimeout(finishClose, 640); // fallback if transitionend never fires
    } else {
      finishClose();
    }
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  window.neogenOpenCart = openCart;
  window.neogenCloseCart = closeCart;

  /* ---------- open triggers (progressive enhancement) ------------------------ */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-cart-link]');
    if (opener && drawer) { e.preventDefault(); openCart(); }
  });

  if (scrim) scrim.addEventListener('click', closeCart);

  /* ---------- cart data: fetch + render -------------------------------------- */
  function cartHeaders() {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  function refreshCart() {
    return fetch(CART_JSON_URL, { headers: cartHeaders(), credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('cart_fetch_failed'); return r.json(); })
      .then(function (cart) { renderCart(cart); clearError(); return cart; })
      .catch(function () { showError('We could not load your bag. Please refresh and try again.'); });
  }

  function updateCartCountBadges(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
  }

  function renderLineItem(item) {
    var subsOn = drawer && drawer.dataset.subscriptionsEnabled === 'true';
    var titleParts = String(item.product_title || item.title || '').split(' — ');
    var baseName = escapeHtml(titleParts[0] || '');
    var suffix = titleParts.length > 1 ? escapeHtml(titleParts.slice(1).join(' — ')) : '';

    var metaParts = [];
    if (item.variant_title && item.variant_title !== 'Default Title') metaParts.push(escapeHtml(item.variant_title));
    if (item.product_type) metaParts.push(escapeHtml(item.product_type));
    var metaHtml = metaParts.length
      ? '<div class="nc-line-item__meta">' + metaParts.map(function (p, i) {
          return (i > 0 ? '<span class="nc-line-item__meta-divider"></span>' : '') + p;
        }).join('') + '</div>'
      : '';

    var priceHtml;
    if (item.original_line_price !== item.final_line_price) {
      priceHtml = '<span class="nc-line-item__price-strike">' + money(item.original_line_price) + '</span>' +
                  '<span class="nc-line-item__price-sub" data-price-sub>' + money(item.final_line_price) + '</span>';
    } else {
      priceHtml = money(item.final_line_price);
    }

    var thumbHtml = item.image
      ? '<img class="nc-line-item__thumb-img" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" width="400" height="500" loading="lazy">'
      : '<span class="nc-line-item__thumb--empty" aria-hidden="true"></span>';

    var perUnit = item.quantity ? (item.original_line_price / item.quantity / 100) : 0;

    var subHtml = '';
    if (subsOn) {
      subHtml =
        '<label class="nc-sub-toggle" data-state="off">' +
          '<input class="nc-sub-toggle__input" type="checkbox" aria-label="Subscribe and save twenty percent" data-sub-toggle>' +
          '<span class="nc-sub-toggle__switch" aria-hidden="true"><span class="nc-sub-toggle__knob" aria-hidden="true"></span></span>' +
          '<span class="nc-sub-toggle__label">' +
            '<span class="nc-sub-toggle__title">Subscribe &amp; save 20%</span>' +
            '<span class="nc-sub-toggle__sub">20% off every order &middot; delivers every ' + DEFAULT_CADENCE + ' days &middot; skip or cancel anytime</span>' +
          '</span>' +
        '</label>' +
        '<div class="nc-sub-cadence" data-cadence-group hidden>' +
          '<span class="nc-sub-cadence__label">Deliver every</span>' +
          '<span class="nc-sub-cadence__opts" role="group" aria-label="Delivery cadence in days">' +
            CADENCES.map(function (d) {
              return '<button class="nc-sub-cadence__opt' + (d === DEFAULT_CADENCE ? ' is-active' : '') + '" type="button" data-cadence="' + d + '" aria-pressed="' + (d === DEFAULT_CADENCE) + '">' + d + '</button>';
            }).join('') +
          '</span>' +
          '<span class="nc-sub-cadence__unit">days</span>' +
        '</div>';
    }

    return (
      '<article class="nc-line-item" data-line-item data-line-key="' + escapeHtml(item.key) + '" data-list-price="' + perUnit.toFixed(2) + '">' +
        '<div class="nc-line-item__thumb">' + thumbHtml + '</div>' +
        '<div class="nc-line-item__content">' +
          '<div class="nc-line-item__head">' +
            '<h3 class="nc-line-item__name">' + baseName + (suffix ? ' <span class="nc-line-item__name-suffix">&mdash; ' + suffix + '</span>' : '') + '</h3>' +
            '<div class="nc-line-item__price" data-price>' + priceHtml + '</div>' +
          '</div>' +
          metaHtml +
          '<div class="nc-line-item__controls">' +
            '<div class="nc-qty" role="group" aria-label="Quantity">' +
              '<button class="nc-qty__btn" type="button" aria-label="Decrease quantity" data-quantity-decrease' + (item.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
              '<span class="nc-qty__val" data-quantity-value aria-live="polite">' + item.quantity + '</span>' +
              '<button class="nc-qty__btn" type="button" aria-label="Increase quantity" data-quantity-increase>+</button>' +
            '</div>' +
            '<button class="nc-line-item__remove" type="button" data-line-remove>Remove</button>' +
          '</div>' +
          subHtml +
        '</div>' +
      '</article>'
    );
  }

  function renderCart(cart) {
    if (!drawer) return;
    updateCartCountBadges(cart.item_count);

    var titleCount = drawer.querySelector('[data-cart-item-count-label]');
    var filledEls = drawer.querySelectorAll('[data-cart-filled]');
    var emptyEl = drawer.querySelector('[data-cart-empty]');
    var titleWrap = drawer.querySelector('.nc-drawer__title');

    if (!cart.item_count) {
      filledEls.forEach(function (el) { el.hidden = true; });
      if (emptyEl) emptyEl.hidden = false;
      if (titleCount) titleCount.remove();
      drawer.setAttribute('aria-label', 'Bag — empty');
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    filledEls.forEach(function (el) { el.hidden = false; });
    drawer.setAttribute('aria-label', 'Bag');

    if (titleWrap) {
      var label = '&middot; ' + cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : '');
      if (titleCount) {
        titleCount.innerHTML = label;
      } else {
        titleCount = document.createElement('span');
        titleCount.className = 'nc-drawer__title-count';
        titleCount.setAttribute('data-cart-item-count-label', '');
        titleCount.innerHTML = label;
        titleWrap.appendChild(titleCount);
      }
    }

    var body = drawer.querySelector('[data-cart-body]');
    if (body) body.innerHTML = cart.items.map(renderLineItem).join('');

    var subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    var checkoutAmountEl = drawer.querySelector('[data-cart-checkout-amount]');
    if (checkoutAmountEl) checkoutAmountEl.textContent = money(cart.total_price);
  }

  /* ---------- /cart/change.js — quantity + remove ---------------------------- */
  function changeLine(key, quantity) {
    var line = drawer.querySelector('[data-line-item][data-line-key="' + key + '"]');
    if (line) line.classList.add('is-loading');
    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: cartHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
      // STUB: bind { selling_plan: planIdFor(cadence) } here once the subscription
      // app is installed and selling plans exist, so 20% + cadence persist to
      // checkout — unblocked by subscription app install, post-launch.
    })
      .then(function (r) { return r.json().then(function (data) { if (!r.ok) throw data; return data; }); })
      .then(function (cart) {
        if (cart.errors) { showError(typeof cart.errors === 'string' ? cart.errors : 'That change could not be completed.'); return; }
        clearError();
        renderCart(cart);
        announce(quantity === 0 ? 'Item removed from your bag.' : 'Quantity updated to ' + quantity + '.');
      })
      .catch(function (err) {
        showError((err && (err.description || err.message)) || 'We could not update your bag. Please try again.');
      })
      .finally(function () { if (line) line.classList.remove('is-loading'); });
  }

  /* ---------- /cart/add.js — exposed for future quick-add / upsell use -------- */
  function addLine(variantId, quantity, sellingPlanId) {
    var payload = { id: variantId, quantity: quantity || 1 };
    if (sellingPlanId) payload.selling_plan = sellingPlanId; // STUB: real selling-plan IDs — post-launch
    return fetch(CART_ADD_URL, {
      method: 'POST', headers: cartHeaders(), credentials: 'same-origin', body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json().then(function (data) { if (!r.ok) throw data; return data; }); })
      .then(function () { clearError(); return refreshCart(); })
      .then(function () { openCart(); })
      .catch(function (err) {
        showError((err && (err.description || err.message)) || 'That item could not be added to your bag.');
        throw err;
      });
  }

  /* ---------- subscribe-&-save preview (client-side only; gated OFF at launch) */
  function activeCadence(line) {
    var active = line.querySelector('[data-cadence].is-active, [data-cadence][aria-pressed="true"]');
    return active ? active.getAttribute('data-cadence') : String(DEFAULT_CADENCE);
  }

  function syncSubscribePreview(line) {
    var toggle = line.querySelector('[data-sub-toggle]');
    if (!toggle) return;
    var wrap = line.querySelector('.nc-sub-toggle');
    var title = line.querySelector('.nc-sub-toggle__title');
    var sub = line.querySelector('.nc-sub-toggle__sub');
    var cadenceGroup = line.querySelector('[data-cadence-group]');
    var priceEl = line.querySelector('[data-price]');
    var qtyEl = line.querySelector('[data-quantity-value]');
    var listPerUnit = parseFloat(line.getAttribute('data-list-price') || '0');
    var qty = qtyEl ? (parseInt(qtyEl.textContent, 10) || 1) : 1;
    var days = activeCadence(line);
    var isOn = toggle.checked;

    if (wrap) { wrap.classList.toggle('is-on', isOn); wrap.dataset.state = isOn ? 'on' : 'off'; }
    toggle.setAttribute('aria-label', isOn ? 'Subscribed, saving twenty percent' : 'Subscribe and save twenty percent');
    if (cadenceGroup) cadenceGroup.hidden = !isOn;
    if (title) title.textContent = isOn ? 'Subscribed · 20% off' : 'Subscribe & save 20%';
    if (sub) sub.textContent = isOn
      ? 'Next delivery in ' + days + ' days · skip or adjust anytime'
      : '20% off every order · delivers every ' + days + ' days · skip or cancel anytime';

    if (priceEl && listPerUnit) {
      var lineListCents = Math.round(listPerUnit * qty * 100);
      if (isOn) {
        priceEl.innerHTML =
          '<span class="nc-line-item__price-strike">' + money(lineListCents) + '</span>' +
          '<span class="nc-line-item__price-sub" data-price-sub>' + money(Math.round(lineListCents * (1 - SUB_RATE))) + '</span>';
      } else {
        priceEl.textContent = money(lineListCents);
      }
    }
  }

  /* ---------- delegated events inside the drawer ------------------------------ */
  if (drawer) {
    drawer.addEventListener('click', function (e) {
      var t = e.target;

      var closer = t.closest && t.closest('[data-cart-close]');
      if (closer) { e.preventDefault(); closeCart(); return; }

      var dec = t.closest && t.closest('[data-quantity-decrease]');
      var inc = t.closest && t.closest('[data-quantity-increase]');
      var remove = t.closest && t.closest('[data-line-remove]');
      if (dec || inc || remove) {
        var line = t.closest('[data-line-item]');
        if (!line) return;
        var key = line.getAttribute('data-line-key');
        var valEl = line.querySelector('[data-quantity-value]');
        var current = valEl ? (parseInt(valEl.textContent, 10) || 1) : 1;
        var next = remove ? 0 : (inc ? current + 1 : Math.max(0, current - 1));
        changeLine(key, next);
        return;
      }

      var cadenceBtn = t.closest && t.closest('[data-cadence]');
      if (cadenceBtn) {
        var cLine = cadenceBtn.closest('[data-line-item]');
        if (!cLine) return;
        cLine.querySelectorAll('[data-cadence]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
          b.classList.remove('is-active');
        });
        cadenceBtn.setAttribute('aria-pressed', 'true');
        cadenceBtn.classList.add('is-active');
        syncSubscribePreview(cLine);
        // STUB: setSellingPlan(key, cadenceBtn.dataset.cadence) — /cart/change.js
        // { selling_plan } — unblocked by subscription app install, post-launch.
        return;
      }
    });

    drawer.addEventListener('change', function (e) {
      var toggle = e.target.closest && e.target.closest('[data-sub-toggle]');
      if (!toggle) return;
      var line = toggle.closest('[data-line-item]');
      if (line) syncSubscribePreview(line);
      // STUB: setSellingPlan(key, toggle.checked ? planIdFor(activeCadence(line)) : null)
      // via /cart/change.js { selling_plan } — unblocked by subscription app
      // install, post-launch (no selling-plan IDs exist until then).
    });
  }

  /* ---------- theme pub/sub integration (add-to-cart elsewhere on the page) --- */
  function wirePubSub() {
    // subscribe/publish/PUB_SUB_EVENTS are declared with `const`/function at the
    // top level of assets/pubsub.js + assets/global.js (classic scripts). `typeof`
    // is the safe way to feature-detect them regardless of script load order.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refreshCart().then(openCart); });
    }
    if (PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, function (data) {
        showError((data && (data.message || data.errors)) || 'That item could not be added to your bag.');
      });
    }
  }
  wirePubSub();

  /* ---------- initial hydration ------------------------------------------------ */
  if (drawer) refreshCart();

  window.neogenCart = {
    SUB_RATE: SUB_RATE,
    CADENCES: CADENCES,
    DEFAULT_CADENCE: DEFAULT_CADENCE,
    open: openCart,
    close: closeCart,
    refresh: refreshCart,
    addLine: addLine
  };
})();

/* sync-bump 2026-07-08T2 */
